import React, { useState } from 'react';
import { AvatarSize } from '@/services/enums';

interface TelegramAvatarProps {
  photoUrl?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  isPremium?: boolean;
  size?: AvatarSize;
  className?: string;
  showStatus?: boolean;
  showPremium?: boolean;
  animated?: boolean;
}

const TelegramAvatar: React.FC<TelegramAvatarProps> = ({
  photoUrl,
  firstName,
  lastName,
  username,
  isPremium = false,
  size = 'lg',
  className = '',
  showStatus = true,
  showPremium = true,
  animated = true,
}) => {
  const [imageError, setImageError] = useState(false);

  // 获取头像尺寸类名
  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8 text-xs';
      case 'md':
        return 'w-12 h-12 text-sm';
      case 'lg':
        return 'w-16 h-16 text-lg';
      case 'xl':
        return 'w-20 h-20 text-xl';
      case '2xl':
        return 'w-24 h-24 text-2xl';
      default:
        return 'w-16 h-16 text-lg';
    }
  };

  // 生成用户名的首字母
  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (username) {
      return username.charAt(0).toUpperCase();
    }
    return '?';
  };

  // 生成 Telegram 风格的渐变背景色
  const getTelegramGradient = () => {
    const name = firstName || username || 'user';
    const gradients = [
      'from-blue-500 to-blue-600', // Telegram 蓝
      'from-green-500 to-green-600', // 绿色
      'from-purple-500 to-purple-600', // 紫色
      'from-orange-500 to-orange-600', // 橙色
      'from-pink-500 to-pink-600', // 粉色
      'from-cyan-500 to-cyan-600', // 青色
      'from-indigo-500 to-indigo-600', // 靛蓝
      'from-teal-500 to-teal-600', // 青绿
    ];

    // 根据用户名生成一致的背景色
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  };

  return (
    <div className={`relative ${className}`}>
      {/* 头像容器 */}
      <div
        className={`
          ${getSizeClass()}
          rounded-full overflow-hidden
          bg-gradient-to-br ${getTelegramGradient()}
          flex items-center justify-center
          text-white font-bold
          shadow-lg
          border-2 border-white/20
          backdrop-blur-sm
          transition-all duration-300
          ${animated ? 'hover:scale-105 hover:shadow-xl' : ''}
          ${animated ? 'animate-pulse-glow' : ''}
        `}
      >
        {photoUrl && !imageError ? (
          <img
            src={photoUrl}
            alt={`${firstName || username || 'User'} avatar`}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="select-none">{getInitials()}</span>
        )}
      </div>

      {/* Premium 标识 */}
      {isPremium && showPremium && (
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}

      {/* 在线状态指示器 */}
      {showStatus && (
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-lg">
          <div className="w-full h-full bg-green-400 rounded-full animate-pulse"></div>
        </div>
      )}

      {/* 头像光环效果 */}
      {animated && (
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400/20 to-blue-500/20 animate-ping"></div>
      )}
    </div>
  );
};

export default TelegramAvatar;
